import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, Button } from "react-bootstrap";
import UserAvatar from "../UserAvatar";
import {
  getAllBoatTypes,
  getActiveVerifiedBoatTypes,
  getRenterVerificationStatus,
  getUserByID,
  getUserMetaByID,
  getVerifiableBoatTypes,
  isVerificationStatusActive,
} from "../utils";

const getStatusMeta = (status) => {
  if (isVerificationStatusActive(status)) {
    return { label: "Verified", className: "renter-profile-status--verified" };
  }
  if (String(status || "").toLowerCase() === "pending") {
    return { label: "Pending Review", className: "renter-profile-status--pending" };
  }
  if (String(status || "").toLowerCase() === "reject") {
    return { label: "Denied", className: "renter-profile-status--denied" };
  }
  return { label: "Not Verified", className: "renter-profile-status--unset" };
};

const formatLocation = (user) => {
  const parts = [user?.city, user?.state, user?.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

const formatMemberSince = (createdDate) => {
  if (!createdDate) return null;
  const date = new Date(createdDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default function RenterProfile() {
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [userMeta, setUserMeta] = useState(null);
  const [boatTypes, setBoatTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError("");

        const [user, meta, types] = await Promise.all([
          getUserByID(userId),
          getUserMetaByID(userId),
          getAllBoatTypes(),
        ]);

        if (!user || user.role !== "Renter" || user.isDeleted) {
          setError("Renter profile not found.");
          setUserData(null);
          return;
        }

        setUserData(user);
        setUserMeta(meta);
        setBoatTypes(getVerifiableBoatTypes(types));
      } catch (loadError) {
        console.error("Error loading renter profile:", loadError);
        setError("Unable to load renter profile.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId]);

  if (loading) {
    return <div className="p-4">Loading renter profile...</div>;
  }

  if (error || !userData) {
    return (
      <div className="p-4">
        <p className="text-muted mb-3">{error || "Renter profile not found."}</p>
        <Button as={Link} to="/dashboard/inbox" variant="outline-secondary">
          Back to Inbox
        </Button>
      </div>
    );
  }

  const fullName = `${userData.firstName || ""} ${userData.lastName || ""}`.trim();
  const verifiedTypes = getActiveVerifiedBoatTypes(userData, userMeta);
  const location = formatLocation(userData);
  const memberSince = formatMemberSince(userData.createdDate);

  return (
    <>
      <div className="page-description">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h1 className="mb-0">Renter Profile</h1>
          <Button as={Link} to="/dashboard/inbox" variant="outline-secondary" size="sm">
            Back to Inbox
          </Button>
        </div>
      </div>

      <Row className="mt-3">
        <Col lg={4} className="mb-4">
          <div className="card renter-profile-card border-0 shadow-sm h-100">
            <div className="card-body text-center p-4">
              <UserAvatar
                src={userData.avatar}
                name={fullName}
                size={120}
                className="mb-3"
              />
              <h4 className="mb-1">{fullName || "Renter"}</h4>
              {userData.bbrId && (
                <p className="text-muted mb-3">{userData.bbrId}</p>
              )}

              <div className="renter-profile-info-list text-start">
                {location && (
                  <div className="renter-profile-info-item">
                    <span className="renter-profile-info-label">Location</span>
                    <span>{location}</span>
                  </div>
                )}
                {memberSince && (
                  <div className="renter-profile-info-item">
                    <span className="renter-profile-info-label">Member since</span>
                    <span>{memberSince}</span>
                  </div>
                )}
                {verifiedTypes.length > 0 && (
                  <div className="renter-profile-info-item">
                    <span className="renter-profile-info-label">Verified for</span>
                    <span>{verifiedTypes.length} boat type{verifiedTypes.length === 1 ? "" : "s"}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>

        <Col lg={8}>
          <div className="card renter-profile-card border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-1">Boat verification status</h5>
                  <p className="text-muted mb-0 small">
                    Verification helps confirm this renter can operate specific boat types.
                  </p>
                </div>
                {verifiedTypes.length > 0 && (
                  <span className="renter-profile-verified-count">
                    <i className="material-icons" aria-hidden="true">
                      verified
                    </i>
                    {verifiedTypes.length} verified
                  </span>
                )}
              </div>

              {verifiedTypes.length > 0 && (
                <div className="renter-profile-verified-summary mb-4">
                  <strong>Verified boat types</strong>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {verifiedTypes.map((boatType) => (
                      <span
                        key={boatType}
                        className="renter-profile-verified-badge"
                      >
                        <i className="material-icons" aria-hidden="true">
                          check_circle
                        </i>
                        {boatType}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="renter-profile-boat-grid">
                {boatTypes.map((boatType) => {
                  const status = getRenterVerificationStatus(
                    userData,
                    userMeta,
                    boatType
                  );
                  const statusMeta = getStatusMeta(status);

                  return (
                    <div key={boatType} className="renter-profile-boat-card">
                      <span className="renter-profile-boat-card__name">
                        {boatType}
                      </span>
                      <span
                        className={`renter-profile-status-pill ${statusMeta.className}`}
                      >
                        {statusMeta.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {verifiedTypes.length === 0 && (
                <p className="text-muted mb-0 mt-3 small">
                  This renter has not completed verification for any boat types yet.
                </p>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
}
